import React, { useEffect, useState } from 'react';
import { TrainingDrill, AgeGroup, SimilarDrillResult, FindSimilarDrillsRequest } from '../Model';
import { restClient } from '../RestClient';
import { Star } from 'lucide-react';
import { getDifficultyColor } from '../Drills/drillColors';
import { YouTubeEmbed, getYouTubeVideoId } from '../YouTubeEmbed';

interface DrillSwapModalProps {
  currentDrill: TrainingDrill;
  timeBlockDuration: number;
  sessionAgeGroup?: AgeGroup;
  onSwap: (newDrill: TrainingDrill) => void;
  onClose: () => void;
}

export const DrillSwapModal: React.FC<DrillSwapModalProps> = ({
  currentDrill,
  timeBlockDuration,
  sessionAgeGroup,
  onSwap,
  onClose,
}) => {
  const [similarDrills, setSimilarDrills] = useState<SimilarDrillResult[]>([]);
  const [filteredDrills, setFilteredDrills] = useState<SimilarDrillResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDrill, setSelectedDrill] = useState<TrainingDrill | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDrillId, setExpandedDrillId] = useState<number | null>(null);

  // Load similar drills on mount
  useEffect(() => {
    const loadSimilarDrills = async () => {
      setIsLoading(true);
      setError(null);

      const request: FindSimilarDrillsRequest = {
        currentDrillId: currentDrill.id,
        category: currentDrill.id < 0 ? 'Game' : currentDrill.category,
        timeBlockDuration: timeBlockDuration,
        skills: currentDrill.skills,
        ageGroup: sessionAgeGroup,
        difficulty: currentDrill.difficulty,
      };

      const result = await restClient.findSimilarDrills(request);

      if (result.IsSuccess) {
        setSimilarDrills(result.Value.drills);
        setFilteredDrills(result.Value.drills);
      } else {
        setError(result.Error?.description ?? 'Failed to load similar drills');
      }

      setIsLoading(false);
    };

    loadSimilarDrills();
  }, [currentDrill, timeBlockDuration, sessionAgeGroup]);

  // Filter drills by search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredDrills(similarDrills);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredDrills(
        similarDrills.filter(
          (result) =>
            result.drill.name.toLowerCase().includes(query) ||
            result.drill.description.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, similarDrills]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleSwap = () => {
    if (selectedDrill) {
      onSwap(selectedDrill);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="swap-modal-title"
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div className="flex-1">
            <h2 id="swap-modal-title" className="modal-title">Swap Drill</h2>
            <p className="text-muted text-sm m-0">
              Replacing: <strong>{currentDrill.name}</strong> ({currentDrill.duration} min)
            </p>
          </div>
          <button onClick={onClose} className="btn-close btn-close-lg ml-4" aria-label="Close">
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search drills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control w-full"
            />
          </div>

          {/* Status */}
          <div className="text-muted text-sm mb-3">
            {isLoading
              ? 'Loading similar drills...'
              : error
              ? ''
              : `${filteredDrills.length} compatible drill${filteredDrills.length !== 1 ? 's' : ''} found`}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="text-center p-6">
              <div className="text-muted">Loading...</div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {/* No Results */}
          {!isLoading && !error && filteredDrills.length === 0 && (
            <div className="text-center p-6">
              <p className="text-muted m-0">
                {searchQuery
                  ? 'No drills match your search.'
                  : 'No compatible drills found that fit within this time block.'}
              </p>
            </div>
          )}

          {/* Drill List */}
          {!isLoading && !error && filteredDrills.length > 0 && (
            <div className="drill-swap-list">
              {filteredDrills.map((result) => {
                const isSelected = selectedDrill?.id === result.drill.id;
                const isExpanded = expandedDrillId === result.drill.id;
                const difficultyColors = getDifficultyColor(result.drill.difficulty);
                const videoId = result.drill.youtubeUrl ? getYouTubeVideoId(result.drill.youtubeUrl) : null;

                return (
                  <div
                    key={result.drill.id}
                    className={`drill-swap-item ${isSelected ? 'drill-swap-item-selected' : ''}`}
                  >
                    {/* Clickable header area for selection */}
                    <div onClick={() => setSelectedDrill(result.drill)}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="drill-swap-name">{result.drill.name}</span>
                            {result.isFavourite && (
                              <Star size={14} fill="currentColor" className="drill-swap-favourite-star" />
                            )}
                            <span
                              className="badge badge-sm"
                              style={{
                                backgroundColor: difficultyColors.bg,
                                color: difficultyColors.text,
                                border: `1px solid ${difficultyColors.border}`,
                              }}
                            >
                              {result.drill.difficulty}
                            </span>
                            <span className="badge badge-sm badge-secondary">
                              {result.drill.duration} min
                            </span>
                          </div>
                          {result.matchingSkills.length > 0 && (
                            <div className="text-sm text-muted">
                              Matching: {result.matchingSkills.join(', ')}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div
                            className="text-sm font-semibold"
                            style={{
                              color: result.matchScore >= 70
                                ? 'var(--color-success)'
                                : result.matchScore >= 40
                                ? 'var(--color-warning)'
                                : 'var(--color-text-muted)',
                            }}
                          >
                            {result.matchScore}%
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expand/Collapse button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDrillId(isExpanded ? null : result.drill.id);
                      }}
                      className="drill-swap-expand-btn"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? `Collapse ${result.drill.name}` : `Expand ${result.drill.name}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`drill-swap-expand-icon ${isExpanded ? 'drill-swap-expand-icon-rotated' : ''}`}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                      <span>{isExpanded ? 'Hide details' : 'View details'}</span>
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="drill-swap-expanded">
                        {/* Description */}
                        <p className="text-sm mb-3">{result.drill.description}</p>

                        {/* Skills */}
                        {result.drill.skills.length > 0 && (
                          <div className="drill-swap-skills">
                            <div className="text-sm text-muted drill-swap-skills-label">Skills:</div>
                            <div className="flex flex-wrap gap-2">
                              {result.drill.skills.map((skill) => (
                                <span key={skill} className="badge badge-sm badge-secondary">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* YouTube Video */}
                        {videoId && result.drill.youtubeUrl && (
                          <div className="drill-swap-video">
                            <YouTubeEmbed
                              url={result.drill.youtubeUrl}
                              title={`${result.drill.name} - training video`}
                              borderRadius={6}
                            />
                          </div>
                        )}

                        {/* No video message */}
                        {!result.drill.youtubeUrl && (
                          <div className="text-sm text-muted drill-swap-no-video">
                            No video available for this drill.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSwap}
            disabled={!selectedDrill}
            className={`btn btn-primary ${!selectedDrill ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Swap Drill
          </button>
        </div>
      </div>
    </div>
  );
};
